import { Controller } from '@hotwired/stimulus'
import zIndexFor from '../../../assets/javascripts/bali/utils/z-index.js'
import { topLayerHost } from '../../../assets/javascripts/bali/utils/top-layer.js'

// Hardcoded instead of letting `dispatch` default to `this.identifier`, so the
// public event names stay put when a host registers this controller under a
// different identifier.
const EVENT_PREFIX = 'bali:hovercard'

const ARROW_SVG = `
<svg width="14" height="8" viewBox="0 0 14 8" fill="none">
  <path d="M14 8L7 8L0 8L7 0L14 8Z" fill="white"/>
  <path d="M7 0.759296L12.8981 7.5L7 7.5L1.10188 7.5L7 0.759296Z" stroke="#1C1817" stroke-opacity="0.2"/>
  <path d="M2.20708 7H11.7911L12.8 8H1.39999L2.20708 7Z" fill="white"/>
</svg>
`

// DaisyUI loading spinner shown while fetching content
const LOADING_SPINNER = `
<div class="hover-card-content flex items-center justify-center p-6">
  <span class="loading loading-spinner loading-md"></span>
</div>
`

/*
  Hovercard controller:
    It generates a hovercard component to show some content
    obtained via a fetch request or template.
*/
export class HovercardController extends Controller {
  static classes = ['content']
  static targets = ['template', 'trigger']
  static values = {
    url: String,
    placement: { type: String, default: 'auto' },
    trigger: { type: String, default: 'mouseenter focus' },
    contentPadding: { type: Boolean, default: true },
    appendTo: { type: String, default: 'body' },
    zIndex: Number,
    arrow: { type: Boolean, default: true }
  }

  async connect () {
    this.contentLoaded = false

    // Use template content, or loading spinner if fetching from URL
    const content = this.getInitialContent()

    const { default: tippy } = await import('tippy.js')

    this.tippy = tippy(this.triggerTarget, {
      allowHTML: true,
      arrow: this.arrowValue ? ARROW_SVG : false,
      duration: 100,
      appendTo: this.appendToProp(),
      content,
      placement: this.placementValue,
      trigger: this.triggerValue,
      interactive: true,
      zIndex: this.hasZIndexValue ? this.zIndexValue : zIndexFor('tooltip'),
      onTrigger: this.onTrigger,
      onCreate: this.onCreate,
      onShow: this.onShow,
      onHide: this.onHide
    })
  }

  getInitialContent () {
    if (this.hasTemplateTarget) {
      return this.templateTarget.innerHTML
    }

    // Show loading spinner when content will be fetched from URL
    if (this.hasUrlValue && this.urlValue.length > 0) {
      return LOADING_SPINNER
    }

    return ''
  }

  // A modal overlay overrides whatever the call site chose: everything outside
  // its subtree is inert, so a card portaled to <body> is painted under the
  // overlay and stops taking pointer events. See utils/top-layer.js. Popper
  // recomputes its offsets against the new offsetParent, so the move is all
  // tippy needs.
  appendToProp () {
    if (this.appendToValue === 'parent') {
      return element => topLayerHost(element) ?? element.parentNode
    }

    if (this.appendToValue === 'body') {
      return element => topLayerHost(element) ?? document.body
    }

    return element =>
      topLayerHost(element) ?? document.querySelector(this.appendToValue) ?? document.body
  }

  disconnect () {
    this.tippy?.destroy()
  }

  onCreate = instance => {
    // ALWAYS add base wrapper class for arrow/shadow styling
    instance.popper.classList.add('hover-card-tippy-wrapper')

    // THEN add any custom content classes (e.g., DaisyUI menu classes)
    if (this.hasContentClass) {
      instance.popper.classList.add(...this.contentClasses)
    }
  }

  onTrigger = () => {
    if (this.hasUrlValue && this.urlValue.length > 0 && !this.contentLoaded) {
      this.loadContent()
    }
  }

  onShow = () => {
    this.element.classList.add('is-active')
    this.dispatch('show', { prefix: EVENT_PREFIX, detail: { tippy: this.tippy } })
  }

  onHide = () => {
    this.element.classList.remove('is-active')
    this.dispatch('hide', { prefix: EVENT_PREFIX, detail: { tippy: this.tippy } })
  }

  async loadContent () {
    const response = await fetch(this.urlValue)
    let html = await response.text()

    if (this.contentPaddingValue) {
      html = `<div class="hover-card-content">${html}</div>`
    }

    this.tippy.setContent(html)
    this.contentLoaded = true
  }
}
